import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { XtellarMark } from '@/src/components/XtellarMark'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  getHelmSessionUser,
  normalizeHelmRedirectPath,
  requestHelmPasswordSession,
} from '@/src/lib/auth'

export function LoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const nextPath = useMemo(
    () => normalizeHelmRedirectPath(searchParams.get('next')),
    [searchParams],
  )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    getHelmSessionUser()
      .then((sessionUser) => {
        if (!cancelled && sessionUser) {
          navigate(nextPath, { replace: true })
        }
      })
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setCheckingSession(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [navigate, nextPath])

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await requestHelmPasswordSession(email, password)
      navigate(nextPath, { replace: true })
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Failed to sign in')
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md rounded-lg">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <XtellarMark />
            <span className="text-sm font-semibold uppercase tracking-[0.14em]">Helm</span>
          </div>
          <CardTitle className="text-center text-2xl font-semibold">Sign in to Helm</CardTitle>
          <CardDescription className="text-center">
            Use your Xtellar account to open this Helm environment.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin} className="space-y-6">
          <CardContent className="space-y-5">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="helm-email">Email</Label>
              <Input
                id="helm-email"
                autoComplete="email"
                type="email"
                placeholder="you@xtellar.app"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="helm-password">Password</Label>
              <Input
                id="helm-password"
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              variant="brand"
              className="w-full"
              disabled={loading || checkingSession}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Need access? Ask a Helm administrator to invite your account.
            </p>
          </CardFooter>
        </form>
      </Card>
    </main>
  )
}
